import { useEffect, useRef } from 'react';
import { useLocalObservable } from 'mobx-react-lite';
import { runInAction } from 'mobx';
import type { AccountSearch } from '@/models/clients/types';
import type { AccountType } from '@/models/account/types';
import { searchAccounts } from '@/models/clients/service';
import { fetchAccountTypes } from '@/models/account/service';
import { getRecentClients, addRecentClient } from '@/lib/recentClients';
import { mapStatus } from '@/lib/clientStatus';
import { getLogin, removeToken, removeLogin } from '@/lib/tokenStorage';

export function useClientSearchController(initialSearch = '') {
  const store = useLocalObservable(() => ({
    results: [] as AccountSearch[],
    isLoading: initialSearch.trim().length > 0,
    error: null as string | null,
    search: initialSearch,
    statusFilter: 'all',
    accountTypeFilter: 'all',
    selectOpen: false,
    typeSelectOpen: false,
    accountTypes: [] as AccountType[],
    recents: getRecentClients(),
    userLogin: getLogin() ?? '',
    get filtered() {
      return this.results.filter(
        (client) =>
          (this.statusFilter === 'all' || mapStatus(client.status_name) === this.statusFilter) &&
          (this.accountTypeFilter === 'all' || client.account_type_name === this.accountTypeFilter)
      );
    },
  }));

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (initialSearch.trim()) fetchData(initialSearch.trim());
    void fetchAccountTypes().then((types) =>
      runInAction(() => {
        store.accountTypes = types;
      })
    );
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps, @eslint-react/exhaustive-deps
  }, []);

  function setSearch(value: string) {
    runInAction(() => {
      store.search = value;
      store.error = null;
    });
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!value.trim()) {
      runInAction(() => {
        store.results = [];
        store.isLoading = false;
      });
      return;
    }
    runInAction(() => {
      store.isLoading = true;
    });
    debounceRef.current = setTimeout(() => fetchData(value.trim()), 400);
  }

  function setStatusFilter(value: string) {
    runInAction(() => {
      store.statusFilter = value;
    });
  }

  function setAccountTypeFilter(value: string) {
    runInAction(() => {
      store.accountTypeFilter = value;
    });
  }

  function setSelectOpen(value: boolean | ((prev: boolean) => boolean)) {
    runInAction(() => {
      store.selectOpen = typeof value === 'function' ? value(store.selectOpen) : value;
    });
  }

  function setTypeSelectOpen(value: boolean | ((prev: boolean) => boolean)) {
    runInAction(() => {
      store.typeSelectOpen = typeof value === 'function' ? value(store.typeSelectOpen) : value;
    });
  }

  function logout() {
    removeToken();
    removeLogin();
  }

  function selectClient(client: AccountSearch) {
    addRecentClient(client);
  }

  async function fetchData(text: string) {
    runInAction(() => {
      store.isLoading = true;
      store.error = null;
    });
    try {
      const results = await searchAccounts(text);
      runInAction(() => {
        store.results = results;
        store.isLoading = false;
      });
    } catch (err) {
      runInAction(() => {
        store.error = err instanceof Error ? err.message : 'Ошибка поиска';
        store.isLoading = false;
      });
    }
  }

  return {
    store,
    setSearch,
    setStatusFilter,
    setAccountTypeFilter,
    setSelectOpen,
    setTypeSelectOpen,
    logout,
    selectClient,
  };
}
